/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment } from 'react'
import { useNavigate } from 'react-router-dom'

import { EuiFlexGroup, EuiFlexItem, EuiLink, EuiSpacer, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useBillingHistoryUrl } from '@modules/urls/billing'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import {
  isCreditCardCustomer,
  isPurchaseOrderCustomer,
} from '@modules/billing-lib/billingDetails/utils'
import { useGetPaymentDetailsV1 } from '@modules/billing-lib/hooks'
import type { PaymentDetails } from '@modules/billing-api/customTypes'

import CreditCardModalButton from '@/components/Billing/CreditCard/CreditCardModalButton'

import CreditCardDetails from './CreditCardDetails'
import { canUpdateCreditCardDetails } from './utils'

interface Props {
  organizationId: string
}

const CreditCardPaymentDetails = ({ organizationId }: Props) => {
  const navigate = useNavigate()
  const billingHistoryUrl = useBillingHistoryUrl(organizationId)
  const billingDetails = useBillingDetails()
  const paymentDetails = useGetPaymentDetailsV1(organizationId)

  if (!billingDetails.data) {
    return null
  }

  return (
    <Fragment>
      <EuiText size='s' data-test-id='payment-details'>
        {isCreditCardCustomer(billingDetails.data) && (
          <FormattedMessage
            id='billing-overview.payment-details.credit-card'
            defaultMessage='Paid by credit card'
          />
        )}

        {isPurchaseOrderCustomer(billingDetails.data) && (
          <FormattedMessage
            id='billing-overview.payment-details.purchase-order'
            defaultMessage='Paid by purchase order'
          />
        )}
      </EuiText>
      <EuiSpacer size='s' />

      <CreditCardDetails organizationId={organizationId} />

      <EuiSpacer size='m' />

      <EuiFlexGroup alignItems='center'>
        {canUpdateCreditCardDetails(billingDetails.data) && (
          <EuiFlexItem grow={false}>
            <CreditCardModalButton
              type='full'
              disabled={paymentDetails.isLoading}
              paymentDetails={paymentDetails.data as PaymentDetails}
            >
              <FormattedMessage
                id='billing-overview.payment-details.update-cc'
                defaultMessage='Update credit card details'
              />
            </CreditCardModalButton>
          </EuiFlexItem>
        )}
        <EuiFlexItem>
          <EuiLink onClick={() => navigate(billingHistoryUrl)}>
            <FormattedMessage
              id='billing-overview.payment-details.view-billing-history'
              defaultMessage='View billing history'
            />
          </EuiLink>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size='l' />

      <EuiText size='s'>
        <FormattedMessage
          id='billing-overview.payments-details.contact-button'
          defaultMessage='<link>Contact us</link> for other forms of payment'
          values={{
            link: (content) => (
              <EuiLink
                external={false}
                href='https://www.elastic.co/contact?baymax=rtp&rogue=eswt-1165-a#sales'
                target='_blank'
              >
                {content}
              </EuiLink>
            ),
          }}
        />
      </EuiText>
    </Fragment>
  )
}

export default CreditCardPaymentDetails
